import React, { useEffect } from 'react';
import { CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import NewRoleAssignmentForm from './forms/NewRoleAssignmentForm';
import { useFetchRoles } from '../hooks/useFetchRoles';
import { useSearchParams } from 'react-router-dom';

const NewRoleAssignmentPage: React.FC = () => {
  const { roles, isLoading, error, fetchRoles, clearError } = useFetchRoles();
  const [searchParams] = useSearchParams();
  const user_id = searchParams.get('user_id') || '';

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleRetry = () => {
    clearError();
    fetchRoles();
  };

  // Sanitize error message for better user experience
  const getErrorMessage = (error: string | null): string => {
    if (!error) return '';

    // Common error messages that should be user-friendly
    if (error.includes('Network Error') || error.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    if (error.includes('401') || error.includes('Unauthorized')) {
      return 'You are not authorized to perform this action. Please refresh the page and try again.';
    }

    if (error.includes('500') || error.includes('Internal Server Error')) {
      return 'Something went wrong on our end. Please try again in a few moments.';
    }

    // For other errors, show a generic message to avoid exposing technical details
    return 'An unexpected error occurred. Please try again.';
  };

  return (
    <div className="min-h-screen flex justify-center p-4">
      <div className="w-full max-w-4xl">
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading roles...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
                <div className="text-destructive mb-4">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-destructive mb-2">
                  Failed to load roles
                </h3>
                <p className="text-destructive/80 mb-4">
                  {getErrorMessage(error)}
                </p>
                <Button
                  onClick={handleRetry}
                  variant="outline"
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <NewRoleAssignmentForm roles={roles} user_id={user_id} />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default NewRoleAssignmentPage;
